import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { ColorModeProvider, useColorMode } from '@/styles/color-mode';
import { lightTheme, darkTheme } from '@/styles/theme';
import { useSessionBootstrap, useAuthState } from '@/features/auth';
import { connectSocket, disconnectSocket } from '@/shared/lib/socket';
import { UpdateAvailableSnackbar } from '@/shared/components/UpdateAvailableSnackbar';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ThemedApp({ children }: { children: React.ReactNode }): React.ReactNode {
  const { resolvedMode } = useColorMode();
  const theme = resolvedMode === 'dark' ? darkTheme : lightTheme;
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

// Kicks off the session-bootstrap query as soon as the QueryClient is in
// scope. AuthGuard subscribes to the same query so it can wait for the
// result; LoginPage's existing redirect-on-authenticated effect handles the
// "user landed on /login but the cookie is still valid" case.
function SessionBootstrapper({ children }: { children: React.ReactNode }): React.ReactNode {
  useSessionBootstrap();
  return <>{children}</>;
}

function SocketConnector({ children }: { children: React.ReactNode }): React.ReactNode {
  const { activeApp, user } = useAuthState();
  React.useEffect(() => {
    if (activeApp?.id && user?.id) {
      void connectSocket(activeApp.id, user.id);
      return () => disconnectSocket();
    }
  }, [activeApp?.id, user?.id]);
  return <>{children}</>;
}

export function Providers({ children }: { children: React.ReactNode }): React.ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionBootstrapper>
        <SocketConnector>
          <ColorModeProvider>
            <ThemedApp>
              {children}
              <UpdateAvailableSnackbar />
            </ThemedApp>
          </ColorModeProvider>
        </SocketConnector>
      </SessionBootstrapper>
    </QueryClientProvider>
  );
}
